import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';

import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import {
    linkifyHtml,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import { ensureProtocol } from '~/modules/url';
import { getImagesSizes } from '~/utils/image-sizes-utils';

export interface ProductInfoItem {
    label: string;
    value: string;
    link?: string | null;
    newTab?: boolean;
}

export interface ProductCustomSection {
    title: string;
    html: string;
}

/**
 * Anchor id for the Product Details accordion ROW (not this content, which
 * Radix unmounts while the row is closed) — the hero's Read more links here.
 * The full-description block leads the row, so landing on the row lands on it.
 */
export const PRODUCT_DETAILS_ANCHOR_ID = 'product-details';

export interface ProductDetailsRowContentProps {
    /** Product title, leading the full-description block. */
    title?: string;
    /**
     * The FULL product description (unbounded CMS copy). Lives here at body
     * scale rather than in the hero, whose statement-size type only suits a
     * short excerpt — the hero clamps to 3 lines and anchors down to this.
     */
    descriptionHtml?: string | null;
    /**
     * Product thumbnail shown in the left column (Figma 1846:9004).
     * `isMultiplied` mirrors the hero gallery's flag: clear-cut product shots
     * sit contained on the fill and multiply into it, photos crop to fill with
     * no blend (a photo multiplied against the fill reads as a dull grey wash).
     */
    image?: { src: string; alt: string; isMultiplied?: boolean };
    /**
     * B3: background colour for the thumbnail container. When the product's
     * default image has `LibraryImageFile.ImageBackgroundColor` set, use it;
     * otherwise the default translucent grey applies.
     */
    imageBackgroundColor?: string | null;
    /** Spec-table rows: Product / Sold by / Category / Range / Colour / … */
    items: ProductInfoItem[];
    /** Rich-text scope-of-use copy, rendered full-width under the table. */
    scopeOfUseHtml?: string | null;
    /** Custom CMS tabs (e.g. "Motion technologies", "Programme") folded in as
     *  labelled rich-text rows after the spec table. */
    customSections?: ProductCustomSection[];
}

/** Sizes for the 746px (at the 1920 anchor) square thumbnail. */
const THUMBNAIL_SIZES = getImagesSizes({ desktop: 746, mobile: 366 });

const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] text-black max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1.375rem]';
// `break-words` lets a long unbreakable value (e.g. a pasted URL in a CMS facet)
// wrap inside its cell instead of forcing the page to scroll horizontally.
// Text track + fixed arrow track so unlinked rows align with text, not icons.
const VALUE_CLASS =
    'm-0 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1 break-words text-right font-sans font-normal leading-[1.3] text-[#767676] max-md:text-sm md:text-[1.375rem]';
const VALUE_LINE_CLASS =
    'grid w-full grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1';
const LINK_CLASS =
    'underline decoration-black/20 underline-offset-2 transition-colors hover:text-[#1f1f21]';

/**
 * At-rest link affordance for spec-table values — the grey underline alone is
 * too subtle against the grey value text, so linked values also carry a small
 * arrow (per BC-4705 QA feedback). Kept in a fixed column so rows without a
 * link still line up with the text above.
 */
const LinkIndicatorIcon = ({ show }: { show: boolean }) => (
    <span
        className="inline-flex size-[0.85em] shrink-0 items-center justify-center"
        aria-hidden={!show}
    >
        {show ? (
            <ArrowUpRight
                aria-hidden="true"
                strokeWidth={2}
                className="size-[0.85em] -translate-y-[0.05em]"
            />
        ) : null}
    </span>
);

/** A value that is a bare URL (e.g. a Care-guide link pasted into a CMS facet) —
 *  rendered as a clickable external link rather than plain text. Matches `www.`
 *  too (href gets a protocol via `ensureProtocol`), mirroring `linkifyHtml`. */
const isExternalUrl = (value: string) =>
    /^(?:https?:\/\/|www\.)\S+$/i.test(value.trim());
/** Left-aligned rich-text for the folded-in custom sections + scope of use. */
const CUSTOM_HTML_CLASS = cn(
    'm-0 min-w-0 flex-1 font-sans leading-[1.4] font-normal text-[#767676] max-md:text-sm md:text-[1.375rem]',
    RICH_TEXT_HTML_CLASS,
    '[&_table]:w-full [&_td]:py-1',
);
/**
 * A custom section can be genuine prose (Scope of use, "Motion technologies")
 * or a short single-line value — e.g. a CMS "Find a retailer" tab that is just
 * a "View Locations" link. The latter reads as a spec value, so it is
 * right-aligned like the spec rows above it rather than flowing left like a
 * paragraph. Prose (multiple/long blocks) stays left-aligned.
 */
const COMPACT_HTML_CLASS = cn(CUSTOM_HTML_CLASS, 'text-right');
const isCompactCustomHtml = (html: string): boolean => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    const hasBlockStructure = /<(?:ul|ol|h[1-6]|table|blockquote|br)\b/i.test(
        html,
    );
    const paragraphCount = (html.match(/<p\b/gi) ?? []).length;
    return !hasBlockStructure && paragraphCount <= 1 && text.length <= 60;
};

const ROW_CLASS =
    'flex items-start border-b border-black/10 py-[0.8125rem] first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem]';

/**
 * (01) Product Details — square product thumbnail on the left; the right
 * column leads with the product title + full description (the title stands in
 * for a "Product" spec row; on wide screens the description flows in
 * newspaper columns like the category pages' bottom text), then the
 * right-aligned spec table (label in medium ink, value in grey), hairline
 * under every row. The scope-of-use copy and the product's custom CMS tabs
 * (e.g. "Motion technologies", "Programme") fold in as labelled rich-text
 * rows after the spec table rather than rendering separately / as their own
 * numbered accordion rows.
 */
export const ProductDetailsRowContent = ({
    title,
    descriptionHtml,
    image,
    imageBackgroundColor,
    items,
    scopeOfUseHtml,
    customSections = [],
}: ProductDetailsRowContentProps) => (
    <div className="pb-[1.8rem] max-md:pb-4">
        <div
            className={cn(
                'grid max-md:gap-y-6',
                image
                    ? 'md:grid-cols-[46.625rem_1fr] md:gap-x-[14.6875rem]'
                    : 'md:grid-cols-1',
            )}
        >
            {image ? (
                // Sticky on desktop so it follows the long spec/custom content
                // instead of leaving a tall whitespace gap on the left. (The
                // Product Details accordion row opts its content out of
                // overflow-clip while open so this can stick — see
                // use-product-accordion-rows.)
                // B3: apply ImageBackgroundColor when set, keeping the
                // default translucent grey (#c5c5c5/20) as the fallback.
                // Mobile hides the thumbnail — it would repeat the hero the
                // reader just scrolled past, so the row runs text-only there.
                <div
                    className={cn(
                        'relative hidden aspect-square w-full self-start overflow-hidden md:sticky md:top-[3.2rem] md:block',
                        !imageBackgroundColor && 'bg-[#c5c5c5]/20',
                    )}
                    style={
                        imageBackgroundColor
                            ? { backgroundColor: imageBackgroundColor }
                            : undefined
                    }
                >
                    {/* Same treatment as the hero gallery slides: clear-cut
                        images (IsMultiplied) sit contained on the fill and
                        multiply into it; photographs keep the full-bleed crop
                        and no blend mode. */}
                    <Image
                        src={image.src}
                        alt={image.alt}
                        fill
                        fit={image.isMultiplied ? 'contain' : 'cover'}
                        multiplied={Boolean(image.isMultiplied)}
                        sizes={THUMBNAIL_SIZES}
                        fadeIn={false}
                    />
                </div>
            ) : null}

            {/* Right column: title + description lead (the title stands in
                for the old "Product" spec row), spec table follows. */}
            <div className="self-start">
                {title ? (
                    <h3 className="m-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#181818] max-md:text-xl md:text-[2.0625rem]">
                        {title}
                    </h3>
                ) : null}
                {descriptionHtml ? (
                    <div
                        className={cn(
                            // The (unbounded, user-authored) description flows
                            // newspaper-style, echoing the category pages'
                            // bottom text. The image column is fixed-width, so
                            // this column's width tracks the viewport — two
                            // text columns only fit from ~1720px up.
                            'columns-1 gap-10 font-sans text-base leading-[1.5] font-normal text-[#181818] max-md:mt-3 min-[1720px]:columns-2 md:mt-[1.5rem]',
                            // Whole paragraphs / headings / bullets move between
                            // columns rather than shearing mid-block.
                            '[&_h2]:break-inside-avoid [&_h3]:break-inside-avoid [&_h4]:break-inside-avoid [&_li]:break-inside-avoid [&_p]:break-inside-avoid',
                            RICH_TEXT_HTML_CLASS,
                        )}
                        dangerouslySetInnerHTML={{
                            __html: linkifyHtml(descriptionHtml),
                        }}
                    />
                ) : null}
                <dl
                    className={cn(
                        'm-0',
                        (title || descriptionHtml) &&
                            'max-md:mt-8 md:mt-[3.75rem]',
                    )}
                >
                    {items.map((item) => (
                        <div key={item.label} className={ROW_CLASS}>
                            <dt className={LABEL_CLASS}>{item.label}</dt>
                            <dd className={VALUE_CLASS}>
                                {item.link ? (
                                    <Link
                                        to={item.link}
                                        target={
                                            item.newTab ? '_blank' : undefined
                                        }
                                        rel={
                                            item.newTab
                                                ? 'noopener noreferrer'
                                                : undefined
                                        }
                                        className={cn(
                                            LINK_CLASS,
                                            'col-span-2',
                                            VALUE_LINE_CLASS,
                                        )}
                                    >
                                        <span className="min-w-0 text-right">
                                            {item.value}
                                        </span>
                                        <LinkIndicatorIcon show />
                                    </Link>
                                ) : isExternalUrl(item.value) ? (
                                    <a
                                        href={ensureProtocol(item.value.trim())}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(
                                            LINK_CLASS,
                                            'col-span-2',
                                            VALUE_LINE_CLASS,
                                        )}
                                    >
                                        <span className="min-w-0 text-right">
                                            {item.value}
                                        </span>
                                        <LinkIndicatorIcon show />
                                    </a>
                                ) : (
                                    <>
                                        <span className="min-w-0 text-right">
                                            {item.value}
                                        </span>
                                        <LinkIndicatorIcon show={false} />
                                    </>
                                )}
                            </dd>
                        </div>
                    ))}
                    {scopeOfUseHtml ? (
                        <div className={ROW_CLASS}>
                            <dt className={LABEL_CLASS}>Scope of use</dt>
                            <dd
                                className={CUSTOM_HTML_CLASS}
                                dangerouslySetInnerHTML={{
                                    __html: linkifyHtml(scopeOfUseHtml),
                                }}
                            />
                        </div>
                    ) : null}
                    {customSections.map((section) => (
                        <div key={section.title} className={ROW_CLASS}>
                            <dt className={LABEL_CLASS}>{section.title}</dt>
                            <dd
                                className={
                                    isCompactCustomHtml(section.html)
                                        ? COMPACT_HTML_CLASS
                                        : CUSTOM_HTML_CLASS
                                }
                                dangerouslySetInnerHTML={{
                                    __html: linkifyHtml(section.html),
                                }}
                            />
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    </div>
);
